import { useState } from 'react';
import axios from 'axios';
import { Mail, Linkedin, Globe2, ArrowUpRight, CheckCircle2, Loader2 } from 'lucide-react';
import AINetwork from '@/components/AINetwork';
import { CONTACT } from '@/lib/testIds';
import { SERVICES } from '@/lib/content';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: '', email: '', company: '', phone: '', service_interest: '', message: '' };

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm(INITIAL);
      toast.success('Thanks — we\'ll be in touch within one business day.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <AINetwork density={24} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-5">Contact</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter max-w-4xl text-balance">
            Let&apos;s talk <span className="gradient-text">growth.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-white/70 leading-relaxed">
            Share a few details about your goals — we&apos;ll respond within one business day with a
            tailored conversation agenda.
          </p>
        </div>
      </section>

      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Info column */}
            <aside className="lg:col-span-4 space-y-5">
              <div className="rounded-2xl border border-white/10 bg-[#0B1221]/60 p-7">
                <Mail className="w-5 h-5 text-cyan-300 mb-4" />
                <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">Email</div>
                <a href="mailto:hello@skvdigitallabs.com" className="font-heading text-lg hover:text-cyan-300 break-all">
                  hello@skvdigitallabs.com
                </a>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#0B1221]/60 p-7">
                <Linkedin className="w-5 h-5 text-cyan-300 mb-4" />
                <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">LinkedIn</div>
                <a href="https://www.linkedin.com/in/sanjay-varma/" target="_blank" rel="noreferrer noopener" className="font-heading text-lg hover:text-cyan-300 inline-flex items-center gap-2">
                  /sanjay-varma <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#0B1221]/60 p-7">
                <Globe2 className="w-5 h-5 text-cyan-300 mb-4" />
                <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">Serving</div>
                <p className="text-sm text-white/75 leading-relaxed">
                  US · UK · Europe · UAE · APAC · Australia · India
                </p>
              </div>
              <div className="rounded-2xl border-l-2 border-cyan-400 bg-cyan-400/[0.04] p-7">
                <div className="text-xs uppercase tracking-[0.22em] text-cyan-300 mb-2">Response time</div>
                <p className="text-sm text-white/80">Within one business day. Strategy calls scheduled inside 72 hours.</p>
              </div>
            </aside>

            {/* Form column */}
            <div className="lg:col-span-8">
              {success ? (
                <div data-testid={CONTACT.success} className="rounded-3xl border border-cyan-400/30 bg-cyan-400/[0.04] p-10 lg:p-14 text-center">
                  <div className="inline-flex w-16 h-16 rounded-full bg-cyan-400/15 grid place-items-center mb-6">
                    <CheckCircle2 className="w-8 h-8 text-cyan-300" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">Message received.</h2>
                  <p className="text-white/70 max-w-md mx-auto">
                    Thank you for reaching out. A member of our team will respond within one business day
                    with next steps for your strategy conversation.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-cyan-400/40 text-sm"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid={CONTACT.form}
                  className="rounded-3xl border border-white/10 bg-[#0B1221]/60 p-7 lg:p-10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Full Name" required>
                      <input
                        data-testid={CONTACT.name}
                        value={form.name} onChange={update('name')}
                        placeholder="Sanjay Varma"
                        className={inputCls}
                        required
                      />
                    </Field>
                    <Field label="Work Email" required>
                      <input
                        data-testid={CONTACT.email}
                        type="email" value={form.email} onChange={update('email')}
                        placeholder="you@company.com"
                        className={inputCls}
                        required
                      />
                    </Field>
                    <Field label="Company">
                      <input
                        data-testid={CONTACT.company}
                        value={form.company} onChange={update('company')}
                        placeholder="Company Inc."
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        data-testid={CONTACT.phone}
                        value={form.phone} onChange={update('phone')}
                        placeholder="+1 555 123 4567"
                        className={inputCls}
                      />
                    </Field>
                    <div className="md:col-span-2">
                      <Field label="Service Interest">
                        <select
                          data-testid={CONTACT.service}
                          value={form.service_interest} onChange={update('service_interest')}
                          className={`${inputCls} appearance-none pr-10 bg-[#0B1221]`}
                        >
                          <option value="">Select a service…</option>
                          {SERVICES.map((s) => (<option key={s.slug} value={s.title}>{s.title}</option>))}
                          <option value="Multiple / Not sure">Multiple / Not sure</option>
                        </select>
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Tell us about your growth goals" required>
                        <textarea
                          data-testid={CONTACT.message}
                          value={form.message} onChange={update('message')}
                          placeholder="Current revenue stage, key markets, biggest growth blocker…"
                          rows={6}
                          className={`${inputCls} resize-none`}
                          required
                        />
                      </Field>
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-6 border-t border-white/5">
                    <p className="text-xs text-white/50">
                      By submitting, you agree to be contacted by SKV Digital Labs about your inquiry.
                    </p>
                    <button
                      type="submit"
                      data-testid={CONTACT.submit}
                      disabled={submitting}
                      className="btn-shimmer inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-cyan-400 text-[#040914] font-semibold hover:bg-cyan-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Schedule a Consultation <ArrowUpRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = 'w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40 transition-colors';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-brand-ink mb-2">
        {label}{required && <span className="text-cyan-300"> *</span>}
      </span>
      {children}
    </label>
  );
}
